import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaMeta } from 'react-icons/fa6';
import { LuChevronRight, LuPlug, LuPlus, LuArrowRight, LuSearch, LuWebhook } from 'react-icons/lu';
import aviso from '../lib/aviso';
import PageHeader from '../components/PageHeader';
import RecordPanel, { PanelSection } from '../components/RecordPanel';
import '../components/ListKit.css';
import { useRecorte } from '../hooks/useRecorte';
import RdStationSimbolo from '../components/apps/RdStationSimbolo';
import { temFuncionalidade, ehAdministradorDaEmpresa, temPermissao, PERMISSOES } from '../config/permissions';
import {
    RdStationService, RD_STATION_AUTH_MESSAGE, RD_STATION_STATE_KEY, type RdStationConexao,
} from '../services/RdStationService';
import { TrafegoPagoService, TRAFEGO_PAGO_AUTH_MESSAGE, type ContaAnuncio } from '../services/TrafegoPagoService';
import { WebhookEntradaService, type WebhookEntrada } from '../services/WebhookEntradaService';
import { TRAFEGO_PAGO_STATE_KEY } from './TrafegoPagoCallbackPage';
import './AppsPage.css';

/* Apps — a vitrine (docs/designsystem/propostas/marketplace.html, revista em 03/10/2026).
   Prefixo `mk-`.

   O que ele fechou: o cartão é a PORTA da primeira conexão — dali a pessoa faz o que o
   sistema pedir — e, conectado, tudo passa a viver em Conexões. WhatsApp e Instagram não
   entram ("são core") e ficam só em Conexões. Funcionalidade desligada em Sistema não
   aparece (decisão de 30/09). Sem preço e sem "em breve": só o que existe. */

type AppId = 'rd' | 'anuncios' | 'webhook';

interface EstadoApp { rotulo: string; classe: 'is-on' | 'is-plano' | 'is-warn' }

interface DefinicaoApp {
    id: AppId;
    nome: string;
    categoria: string;
    descricao: string;
    icone: ReactNode;
    classe: string;
    /** O que a integração faz, em passos — o corpo do painel. */
    faz: string[];
}

const APPS: Record<AppId, DefinicaoApp> = {
    rd: {
        id: 'rd',
        nome: 'RD Station Marketing',
        categoria: 'Marketing e aquisição',
        descricao: 'Quem converte no RD vira oportunidade aqui — e a venda volta para lá.',
        icone: <RdStationSimbolo size={15} />,
        classe: 'is-rd',
        faz: [
            'Cada conversão no RD (formulário, landing page) entra como contato e abre uma oportunidade na primeira etapa da Pipeline.',
            'O contato é casado pelo telefone e, sem telefone, pelo e-mail — ninguém fica de fora por ter deixado só o e-mail.',
            'Quando a oportunidade avança, é ganha ou perdida aqui, o RD fica sabendo — para a automação de marketing seguir o funil de verdade.',
        ],
    },
    anuncios: {
        id: 'anuncios',
        nome: 'Anúncios da Meta',
        categoria: 'Marketing e aquisição',
        descricao: 'O gasto das campanhas do Facebook e Instagram e os leads de formulário no CRM.',
        icone: <FaMeta size={17} />,
        classe: 'is-meta',
        faz: [
            'Lê o gasto e o desempenho de cada campanha da conta de anúncios, todo dia, para a tela de Anúncios.',
            'Os formulários de lead (Lead Ads) do Facebook e do Instagram passam a entrar no CRM.',
            'A conversa que nasce de um anúncio mostra de qual campanha veio.',
        ],
    },
    /* docs/designsystem/propostas/webhook-de-entrada.html — um cartão só; cada fonte vira
       uma linha em Conexões, como um segundo número de WhatsApp vira uma segunda linha. */
    webhook: {
        id: 'webhook',
        nome: 'Webhook de entrada',
        categoria: 'Automação',
        descricao: 'Formulário do site, Make, n8n ou sistema próprio: o lead que chega no endereço vira oportunidade.',
        icone: <LuWebhook size={17} />,
        classe: 'is-wh',
        faz: [
            'Cada fonte (o formulário do site, um cenário do Make ou do n8n) ganha um endereço próprio, com nome.',
            'O que a fonte envia vira contato e abre uma oportunidade na primeira etapa da Pipeline — a pessoa é achada pelo telefone e, sem telefone, pelo e-mail.',
            'Os campos personalizados da empresa podem vir junto, pela chave de cada um.',
        ],
    },
};

const AppsPage = () => {
    const navigate = useNavigate();
    const admin = ehAdministradorDaEmpresa();
    const flag = {
        rd: temFuncionalidade('rd_station'),
        /* As rotas de conta de anúncio pedem a chave `anuncios` na API: sem ela,
           nem busca — senão o 403 levantaria o aviso global ao abrir a tela. */
        anuncios: temFuncionalidade('anuncios') && temPermissao(PERMISSOES.anuncios),
        webhook: temFuncionalidade('webhook_entrada'),
    };

    const [carregando, setCarregando] = useState(true);
    const [rd, setRd] = useState<RdStationConexao | null>(null);
    const [contas, setContas] = useState<ContaAnuncio[]>([]);
    const [entradas, setEntradas] = useState<WebhookEntrada[]>([]);
    const [aberto, setAberto] = useState<AppId | null>(null);
    const [conectando, setConectando] = useState<AppId | null>(null);
    /* Criar entrada pede só o nome da fonte, no próprio painel. */
    const [nomeEntrada, setNomeEntrada] = useState('');
    const [erroEntrada, setErroEntrada] = useState('');

    const carregar = useCallback(async () => {
        const [r, c, w] = await Promise.all([
            flag.rd ? RdStationService.obter() : Promise.resolve(null),
            flag.anuncios ? TrafegoPagoService.buscarContas().catch(() => [] as ContaAnuncio[]) : Promise.resolve([] as ContaAnuncio[]),
            flag.webhook ? WebhookEntradaService.listar() : Promise.resolve(null),
        ]);
        if (r?.sucesso && r.dados) setRd(r.dados);
        setContas(c);
        if (w?.sucesso && w.dados) setEntradas(w.dados);
        setCarregando(false);
        // As chaves vêm do usuário logado e não mudam com a tela aberta.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => { carregar(); }, [carregar]);

    /* Os dois popups avisam a tela pelo postMessage, como em Conexões. */
    useEffect(() => {
        const aoReceber = async (e: MessageEvent) => {
            if (e.origin !== window.location.origin) return;
            if (e.data?.type === RD_STATION_AUTH_MESSAGE) {
                setConectando(null);
                if (e.data.success) { aviso.ok(e.data.mensagem || 'RD Station conectado.'); await carregar(); }
                else aviso.erro(e.data.mensagem || 'Não foi possível conectar o RD Station.');
            }
            if (e.data?.type === TRAFEGO_PAGO_AUTH_MESSAGE) {
                setConectando(null);
                if (e.data.success) { aviso.ok(e.data.mensagem || 'Conta de anúncios conectada.'); await carregar(); }
                else aviso.erro(e.data.mensagem || 'Não foi possível conectar a conta de anúncios.');
            }
        };
        window.addEventListener('message', aoReceber);
        return () => window.removeEventListener('message', aoReceber);
    }, [carregar]);

    const conectar = async (id: AppId) => {
        setConectando(id);
        const state = crypto.randomUUID();
        sessionStorage.setItem(id === 'rd' ? RD_STATION_STATE_KEY : TRAFEGO_PAGO_STATE_KEY, state);
        const r = id === 'rd'
            ? await RdStationService.urlAutorizacao(state)
            : await TrafegoPagoService.getMetaConfig(state);
        const url = r.dados?.urlAutorizacao;
        if (!r.sucesso || !url) {
            aviso.erro(r.mensagem || 'Não foi possível iniciar a conexão.');
            setConectando(null);
            return;
        }
        const popup = window.open(url, `${id}-oauth`, 'width=600,height=750,menubar=no,toolbar=no');
        if (!popup) { aviso.atencao('Permita pop-ups para este site e tente novamente.'); setConectando(null); }
    };

    const visiveis = useMemo(
        () => (Object.keys(APPS) as AppId[]).filter(id => flag[id]),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []);

    /* ── busca e tags de categoria (ideia do marketplace de referência, 03/10) ──
       O recorte sobrevive à ida a Conexões e à volta, como nas listagens. */
    const [recorte, gravarRecorte] = useRecorte<{ busca: string; categoria: string | null }>(
        'mk:recorte', { busca: '', categoria: null });
    const [busca, setBusca] = useState(recorte.busca);
    const [categoria, setCategoria] = useState<string | null>(recorte.categoria);
    useEffect(() => { gravarRecorte({ busca, categoria }); }, [busca, categoria, gravarRecorte]);

    /* Só as categorias que têm app visível — tag que filtra para zero é porta para lugar nenhum. */
    const categorias = useMemo(() => {
        const m = new Map<string, number>();
        visiveis.forEach(id => m.set(APPS[id].categoria, (m.get(APPS[id].categoria) ?? 0) + 1));
        return [...m].sort((a, b) => a[0].localeCompare(b[0], 'pt-BR'));
    }, [visiveis]);
    const categoriaValida = categoria && categorias.some(([c]) => c === categoria) ? categoria : null;

    const filtrados = useMemo(() => {
        const t = busca.trim().toLowerCase();
        return visiveis.filter(id => {
            const a = APPS[id];
            return (!categoriaValida || a.categoria === categoriaValida)
                && (!t || [a.nome, a.descricao, a.categoria].some(v => v.toLowerCase().includes(t)));
        });
    }, [visiveis, busca, categoriaValida]);

    const estado = (id: AppId): EstadoApp => {
        if (id === 'rd') {
            if (!rd?.conectada) return { rotulo: 'Disponível', classe: 'is-plano' };
            if (rd.status === 3) return { rotulo: 'Acesso expirado', classe: 'is-warn' };
            if (!rd.webhookAtivo) return { rotulo: 'Conectado · entrada pendente', classe: 'is-warn' };
            return { rotulo: 'Conectado', classe: 'is-on' };
        }
        if (id === 'webhook') {
            if (entradas.length === 0) return { rotulo: 'Disponível', classe: 'is-plano' };
            return { rotulo: entradas.length === 1 ? '1 entrada' : `${entradas.length} entradas`, classe: 'is-on' };
        }
        if (contas.length === 0) return { rotulo: 'Disponível', classe: 'is-plano' };
        return { rotulo: contas.length === 1 ? '1 conta conectada' : `${contas.length} contas conectadas`, classe: 'is-on' };
    };

    const conectado = (id: AppId) => (id === 'rd' ? !!rd?.conectada : id === 'webhook' ? entradas.length > 0 : contas.length > 0);

    /* Com uma entrada só, "Ver em Conexões" já abre o painel dela; com mais, a lista. */
    const destinoEmConexoes = (id: AppId) => {
        if (id === 'rd') return '/connections?abrir=rd';
        if (id === 'webhook') return entradas.length === 1 ? `/connections?abrir=webhook:${entradas[0].id}` : '/connections';
        return '/connections?aba=anuncios';
    };

    const criarEntrada = async () => {
        const nome = nomeEntrada.trim();
        if (!nome) { setErroEntrada('Dê um nome para saber de onde vêm os leads, como "Formulário do site".'); return; }
        setConectando('webhook');
        const r = await WebhookEntradaService.criar(nome);
        setConectando(null);
        if (!r.sucesso || !r.dados) {
            if (/nome|chamada/i.test(r.mensagem)) setErroEntrada(r.mensagem);
            else aviso.erro(r.mensagem || 'Não foi possível criar a entrada.');
            return;
        }
        aviso.ok('Entrada criada. Copie o endereço e cole na ferramenta que envia os leads.');
        navigate(`/connections?abrir=webhook:${r.dados.entrada.id}`);
    };

    useEffect(() => { setNomeEntrada(''); setErroEntrada(''); }, [aberto]);

    const app = aberto ? APPS[aberto] : null;
    const estadoAberto = aberto ? estado(aberto) : null;
    /* Sem credencial do app só importa para quem ainda não conectou. */
    const rdSemCredencial = aberto === 'rd' && rd !== null && !rd.conectada && !rd.appConfigurado;

    /* A barra de ações do painel é a porta: conectar (só o administrador), ou ir para
       Conexões, onde a conexão é cuidada. Anúncios aceita mais de uma conta; o RD, uma.
       O webhook não conecta conta nenhuma: cria uma entrada, com o nome da fonte. */
    const acoes = app && (
        <>
            {conectado(app.id) && (
                <button type="button" className="rp-btn" onClick={() => navigate(destinoEmConexoes(app.id))}>
                    Ver em Conexões <LuArrowRight size={13} />
                </button>
            )}
            {admin && app.id === 'webhook' && (
                <button type="button" className="rp-btn is-primary" disabled={!!conectando} onClick={criarEntrada}>
                    <LuPlus size={13} />
                    {conectando === 'webhook' ? 'Criando…' : conectado('webhook') ? 'Criar outra entrada' : 'Criar entrada'}
                </button>
            )}
            {admin && app.id !== 'webhook' && (!conectado(app.id) || app.id === 'anuncios') && (
                <button type="button" className="rp-btn is-primary" disabled={!!conectando || rdSemCredencial}
                    onClick={() => conectar(app.id)}>
                    {conectado(app.id) ? <LuPlus size={13} /> : <LuPlug size={13} />}
                    {conectando === app.id ? 'Conectando…' : conectado(app.id) ? 'Conectar outra conta' : 'Conectar'}
                </button>
            )}
        </>
    );

    return (
        <div className="page-shell lk-screen mk-screen">
            <PageHeader
                centered
                title="Apps"
                subtitle="Conecte o CRM ao que a empresa já usa. Depois de conectado, o app passa a ser cuidado em Conexões."
                actions={
                    <button type="button" className="lk-btn" onClick={() => navigate('/connections')}>
                        Minhas conexões <LuChevronRight size={13} />
                    </button>
                }
            />

            <div className="page-body lk-fill">
                {carregando ? (
                    <div className="mk-grade">
                        {visiveis.map(id => <div key={id} className="lk-skeleton" style={{ height: 150, borderRadius: 12 }} />)}
                    </div>
                ) : visiveis.length === 0 ? (
                    <div className="lk-blank">
                        <div className="lk-blank-title">Nenhum app disponível para a sua empresa</div>
                        <p className="lk-blank-text">Quando uma integração for liberada para a empresa, ela aparece aqui.</p>
                    </div>
                ) : (
                    <div className="mk-col">
                    <div className="mk-filtros">
                        <label className="lk-search mk-busca">
                            <LuSearch size={14} />
                            <input type="search" placeholder="Buscar apps…" aria-label="Buscar apps"
                                value={busca} onChange={e => setBusca(e.target.value)} />
                        </label>
                        <div className="mk-tags" role="group" aria-label="Categoria">
                            <button type="button" className={`mk-tag ${categoriaValida === null ? 'is-on' : ''}`}
                                aria-pressed={categoriaValida === null} onClick={() => setCategoria(null)}>
                                Todas <span className="mk-tag-n">{visiveis.length}</span>
                            </button>
                            {categorias.map(([c, n]) => (
                                <button key={c} type="button" className={`mk-tag ${categoriaValida === c ? 'is-on' : ''}`}
                                    aria-pressed={categoriaValida === c} onClick={() => setCategoria(c)}>
                                    {c} <span className="mk-tag-n">{n}</span>
                                </button>
                            ))}
                        </div>
                    </div>
                    {filtrados.length === 0 ? (
                        <div className="lk-blank">
                            <div className="lk-blank-title">Nenhum app com esse filtro</div>
                            <p className="lk-blank-text">Ajuste a busca ou volte para "Todas".</p>
                        </div>
                    ) : (
                    <div className="mk-grade">
                        {filtrados.map(id => {
                            const a = APPS[id];
                            const e = estado(id);
                            return (
                                <div key={id} className="mk-card">
                                    <div className="mk-card-top">
                                        <span className={`mk-bx ${a.classe}`} aria-hidden="true">{a.icone}</span>
                                        <div className="mk-tit">
                                            <div className="mk-nome">{a.nome}</div>
                                            <div className="mk-cat">{a.categoria}</div>
                                        </div>
                                    </div>
                                    <p className="mk-desc">{a.descricao}</p>
                                    <div className="mk-pe">
                                        <span className={`mk-est ${e.classe}`}>{e.rotulo}</span>
                                        <button type="button" className="mk-ver" onClick={() => setAberto(id)}>
                                            Ver detalhes <LuChevronRight size={12} />
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                    )}
                    </div>
                )}
            </div>

            <RecordPanel
                open={!!app}
                onClose={() => setAberto(null)}
                initials={app?.id === 'rd' ? 'RD' : app?.id === 'webhook' ? 'WH' : 'AD'}
                squareAvatar
                title={app?.nome ?? ''}
                subtitle={app ? `${app.categoria} · ${estadoAberto?.rotulo ?? ''}` : ''}
                actions={acoes || undefined}
            >
                {app && (
                    <>
                        <PanelSection title="O que essa integração faz">
                            <ol className="mk-passos">
                                {app.faz.map((t, i) => (
                                    <li key={i}><span className="mk-n">{i + 1}</span><span>{t}</span></li>
                                ))}
                            </ol>
                        </PanelSection>

                        <PanelSection title="Nesta empresa">
                            {app.id === 'webhook' ? (
                                <>
                                    <p className="mk-aviso">
                                        {entradas.length > 0
                                            ? <>{entradas.length === 1 ? 'Uma entrada criada' : `${entradas.length} entradas criadas`}: {entradas.map(e => e.nome).join(', ')}. Elas ficam em Conexões, com o endereço de cada uma.</>
                                            : admin
                                                ? 'Crie uma entrada para cada lugar que manda leads. Ela ganha um endereço próprio, que fica em Conexões para você copiar.'
                                                : 'Peça a um administrador da empresa para criar uma entrada.'}
                                    </p>
                                    {admin && (
                                        <div className="rp-field" style={{ marginTop: 12 }}>
                                            <label className="rp-label" htmlFor="mk-nome-entrada">
                                                {entradas.length > 0 ? 'Nome da nova entrada' : 'Nome da entrada'}
                                            </label>
                                            <input id="mk-nome-entrada" className="rp-input" maxLength={80}
                                                placeholder="Formulário do site" value={nomeEntrada}
                                                aria-invalid={!!erroEntrada}
                                                onChange={e => { setNomeEntrada(e.target.value); setErroEntrada(''); }}
                                                onKeyDown={e => { if (e.key === 'Enter') criarEntrada(); }} />
                                            {erroEntrada
                                                ? <p className="rp-error">{erroEntrada}</p>
                                                : <p className="rp-hint">De onde vêm os leads. É a origem que aparece na oportunidade.</p>}
                                        </div>
                                    )}
                                </>
                            ) : rdSemCredencial ? (
                                <p className="mk-aviso">
                                    A conexão com o RD Station ainda não está liberada nesta instalação: falta a
                                    credencial do app da RD. Assim que ela for cadastrada, o botão Conectar funciona.
                                </p>
                            ) : conectado(app.id) ? (
                                <p className="mk-aviso">
                                    {app.id === 'rd'
                                        ? <>Conectado à conta <strong>{rd?.conta || 'RD Station'}</strong>. A configuração — o que fazer com quem converte, o que volta para o RD e os campos — fica em Conexões.</>
                                        : <>{contas.length === 1 ? 'Uma conta de anúncios conectada' : `${contas.length} contas de anúncios conectadas`}. Elas ficam em Conexões, na aba Contas de anúncio.</>}
                                </p>
                            ) : (
                                <p className="mk-aviso">
                                    {admin
                                        ? (app.id === 'rd'
                                            ? 'Ao conectar, uma janela da RD pede para você entrar na conta e autorizar o CRM. Depois disso a conexão aparece em Conexões, onde se escolhe o que fazer com quem converte.'
                                            : 'Ao conectar, uma janela da Meta pede para você escolher a conta de anúncios. Depois disso ela aparece em Conexões.')
                                        : 'Peça a um administrador da empresa para conectar.'}
                                </p>
                            )}
                        </PanelSection>
                    </>
                )}
            </RecordPanel>
        </div>
    );
};

export default AppsPage;
